import { useEffect, useMemo, useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Bank, CreditCard, Wallet } from 'phosphor-react';
import { Supplier } from '@dill/dill-shared';
import { twMerge } from 'tailwind-merge';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import { updateSupplierDefaultBuyerDisabledPaymentTypes } from '../../../../../../redux/services/suppliersService';
import { getUserDetails } from '../../../../../../redux/services/authService';
import { openMessageModal } from '../../../../../../redux/globalSlices/genericSlice';
import { openDillToast } from '../../../../../../utils/helpers';
import { COLORS } from '../../../../../../utils/colors';
import { DillButton, DillToggle } from '../../../../../general/Dill';
import { AppRadioButton } from '../../../../../general/AppRadioButton/AppRadioButton';

type PaymentType = 'CREDIT_CARD' | 'DEBIT_CARD' | 'BANK';

const PAYMENT_TYPE_META: Record<
  PaymentType,
  { label: string; description: string; Icon: typeof CreditCard }
> = {
  CREDIT_CARD: {
    label: 'Credit Card',
    description: 'Disable credit card payments for your customers',
    Icon: CreditCard,
  },
  DEBIT_CARD: {
    label: 'Debit Card',
    description: 'Disable debit card payments for your customers',
    Icon: Wallet,
  },
  BANK: {
    label: 'Bank Transfer',
    description: 'Disable bank transfer payments for your customers',
    Icon: Bank,
  },
};

const ALL_PAYMENT_TYPES = Object.keys(PAYMENT_TYPE_META) as PaymentType[];

interface DisabledPaymentTypesSettingsSectionProps {
  supplier?: Supplier | null;
}

const getInitialTypes = (supplier?: Supplier | null): PaymentType[] =>
  (supplier?.defaultBuyerDisabledPaymentTypes ?? []) as PaymentType[];

const sortedKey = (types: PaymentType[]) => JSON.stringify([...types].sort());

export const DisabledPaymentTypesSettingsSection = ({
  supplier,
}: DisabledPaymentTypesSettingsSectionProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);

  const canEdit = useMemo(() => {
    if (!currentUser?.email || !supplier) return false;
    const email = currentUser.email.toLowerCase();
    const supplierEmail = supplier.email?.toLowerCase() ?? '';
    const adminEmails = (supplier.adminSupplierEmails ?? []).map((e) => e.toLowerCase());
    return (
      email === supplierEmail ||
      adminEmails.includes(email) ||
      (currentUser.roles?.includes('DILL_ADMIN') ?? false)
    );
  }, [currentUser, supplier]);

  const [disabledTypes, setDisabledTypes] = useState<PaymentType[]>(() =>
    getInitialTypes(supplier)
  );
  const [initialDisabledTypes, setInitialDisabledTypes] = useState<PaymentType[]>(() =>
    getInitialTypes(supplier)
  );
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [saving, setSaving] = useState(false);

  const isDebitCardDisabled = !!supplier?.isDebitCardDisabled;

  useEffect(() => {
    const types = getInitialTypes(supplier);
    setDisabledTypes(types);
    setInitialDisabledTypes(types);
  }, [supplier]);

  const typesChanged = sortedKey(disabledTypes) !== sortedKey(initialDisabledTypes);

  const togglePaymentType = (type: PaymentType) => {
    setDisabledTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const performSave = async () => {
    setSaving(true);
    const result = await dispatch(
      updateSupplierDefaultBuyerDisabledPaymentTypes({
        supplierId: supplier?.id || '',
        defaultBuyerDisabledPaymentTypes: disabledTypes,
        overwriteExisting,
      })
    );
    setSaving(false);

    if (result.type === 'supplier/updateDefaultBuyerDisabledPaymentTypes/fulfilled') {
      setInitialDisabledTypes([...disabledTypes]);
      await dispatch(getUserDetails());
      openDillToast({
        type: 'SUCCESS',
        message: 'Payment methods saved. Customers are being updated in the background.',
      });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to save payment methods' });
    }
  };

  const handleSave = () => {
    if (!typesChanged) {
      return;
    }

    const disabledLabels = disabledTypes.map((type) => PAYMENT_TYPE_META[type].label).join(', ');
    const subTitle = overwriteExisting
      ? `Saving will ${
          disabledTypes.length > 0
            ? `disable ${disabledLabels} for`
            : 'enable all payment methods on'
        } all of your customers. Any payment method customizations made to specific customers will be overwritten.`
      : `Saving will ${
          disabledTypes.length > 0
            ? `disable ${disabledLabels} for`
            : 'enable all payment methods on'
        } customers that have no payment method customizations of their own. Customers you have customized will be left as they are.`;

    dispatch(
      openMessageModal({
        modalType: 'WARN',
        title: overwriteExisting
          ? 'Update payment methods for all customers?'
          : 'Update payment methods for customers without customizations?',
        subTitle,
        buttonText: 'Cancel',
        secondButtonText: 'Proceed',
        isSecondButton: true,
        onSecondButtonClick: async () => {
          await performSave();
        },
      })
    );
  };

  const scopeOptions: { value: boolean; label: string; helper: string }[] = [
    {
      value: false,
      label: 'Only customers without customizations',
      helper: 'Customers with their own disabled payment methods are left untouched',
    },
    {
      value: true,
      label: 'All customers',
      helper: 'Overwrites payment method customizations made to specific customers',
    },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900">Disable Payment Methods</h4>
        <p className="text-xs text-gray-400 mt-0.5">
          Turn on a payment method to stop your customers from paying with it. This applies to new
          customers, and to existing customers based on the option you pick below.
        </p>
      </div>

      <div className="flex flex-col gap-2 flex-1">
        {ALL_PAYMENT_TYPES.map((type) => {
          const { label, description, Icon } = PAYMENT_TYPE_META[type];
          // Debit is already off for the whole account, so it can't be changed here.
          const isSupplierDebitDisabled = type === 'DEBIT_CARD' && isDebitCardDisabled;
          const tooltipId = isSupplierDebitDisabled
            ? 'supplier-debit-disabled-tooltip'
            : !canEdit
            ? 'no-permission-payment-types-tooltip'
            : undefined;
          return (
            <div
              key={type}
              className={twMerge(
                'flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3',
                (!canEdit || isSupplierDebitDisabled) && 'opacity-70'
              )}
              data-tooltip-id={tooltipId}>
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100">
                  <Icon size={16} color={COLORS.GREY_600} weight="bold" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">{label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {isSupplierDebitDisabled
                      ? 'Debit card payments are already disabled for your account'
                      : description}
                  </p>
                </div>
              </div>
              <div className="pt-0.5">
                <DillToggle
                  value={isSupplierDebitDisabled || disabledTypes.includes(type)}
                  onChange={() => togglePaymentType(type)}
                  isDisabled={!canEdit || isSupplierDebitDisabled}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4">
        <p className="text-xs font-medium text-gray-800 mb-2">Apply to</p>
        <div className="flex flex-col gap-2">
          {scopeOptions.map((option) => (
            <div
              key={`${option.value}`}
              role="radio"
              aria-checked={overwriteExisting === option.value}
              aria-disabled={!canEdit}
              tabIndex={!canEdit ? -1 : 0}
              onClick={() => {
                if (canEdit) setOverwriteExisting(option.value);
              }}
              onKeyDown={(e) => {
                if (canEdit && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  setOverwriteExisting(option.value);
                }
              }}
              className={twMerge(
                'flex items-center gap-3 rounded-lg border bg-white px-4 py-3 transition-colors',
                !canEdit
                  ? 'cursor-not-allowed border-gray-200 opacity-70'
                  : 'cursor-pointer border-gray-200 hover:border-gray-300',
                overwriteExisting === option.value &&
                  canEdit &&
                  'border-blue-500 bg-blue-50 hover:border-blue-500'
              )}>
              <AppRadioButton
                isChecked={overwriteExisting === option.value}
                isDisabled={!canEdit}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{option.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{option.helper}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <div data-tooltip-id={!canEdit ? 'no-permission-payment-types-tooltip' : undefined}>
          <DillButton
            onClick={handleSave}
            label="Save changes"
            isLoading={saving}
            isDisabled={!canEdit || !typesChanged}
          />
        </div>
      </div>

      {isDebitCardDisabled && (
        <ReactTooltip
          id="supplier-debit-disabled-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          Debit card payments are disabled for your account
        </ReactTooltip>
      )}

      {!canEdit && (
        <ReactTooltip
          id="no-permission-payment-types-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          You do not have permission to make changes
        </ReactTooltip>
      )}
    </div>
  );
};
